/**
 * ThoughtEngineeringPage — "Thought Engineering · Strategy & Design".
 * The capstone surface: the book's 5-step Molecular Influence pre-flight
 * worksheet for one counterpart, temperament-ranked, from operator context.
 */

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../lib/useSession.js";
import {
  Wordmark, SectionHeader, Callout, AuditFooter, MarkdownV2,
  PrintToPdfButton, RecentBlueprintsPanel, BlueprintJobView,
} from "../components/index.js";
import MentionAutocomplete from "../components/MentionAutocomplete.jsx";
import BlueprintChat from "../components/BlueprintChat.jsx";
import { parseJsonResponse } from "../lib/api-fetch.js";
import { terminalActionButtonStyle, terminalActionButtonDisabledStyle } from "../lib/buttonStyles.js";

const PRESETS = [
  { label: "Sharpen my goal",          preset_text: "My goal feels vague — help me make the exact outcome sharper and more specific." },
  { label: "Run the top draw",         preset_text: "Walk me through exactly how to run the #1 molecular draw you recommended in the first few minutes." },
  { label: "Close the riskiest gap",   preset_text: "Which open logic gap is most likely to kill this, and what's the prebuttal?" },
  { label: "Rehearse the open",        preset_text: "Give me a frame for the first 60 seconds that neutralizes fight-or-flight with this person." },
];

export default function ThoughtEngineeringPage() {
  const { session, appUser } = useSession();

  const [mentions, setMentions] = useState([]);
  const [mentionText, setMentionText] = useState("");
  const [goal, setGoal] = useState("");
  const [goalPrefilled, setGoalPrefilled] = useState(false);

  // Pre-fill the goal from the operator's saved default goals (editable). Only
  // once, and only while the field is still untouched.
  useEffect(() => {
    const defaultGoals = appUser?.operator_context?.goals;
    if (defaultGoals && !goal && !goalPrefilled) {
      setGoal(defaultGoals);
      setGoalPrefilled(true);
    }
  }, [appUser, goal, goalPrefilled]);
  const [wants, setWants] = useState("");
  const [setting, setSetting] = useState("");
  const [exposure, setExposure] = useState("3");
  const [gap, setGap] = useState("");
  const [board, setBoard] = useState("");

  const [generationStarted, setGenerationStarted] = useState(false);
  const [error, setError] = useState(null);
  const [plan, setPlan] = useState(null);

  // Validate, then hand off to the Tier 2 background pipeline (the 6000-token
  // capstone overruns the synchronous function ceiling — produced a 504 on the
  // old sync route). BlueprintJobView starts the job and polls to completion.
  const beginGeneration = () => {
    if (mentions.length < 1) { setError("Mention the counterpart (a saved Profile)."); return; }
    if (!goal.trim()) { setError("Name your exact goal — the specific outcome you want from this encounter."); return; }
    setError(null);
    setGenerationStarted(true);
  };

  const startJob = async () => {
    try {
      const res = await fetch("/.netlify/functions/thought-engineering-start", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({
          counterpart_profile_id: mentions[0].profile_id,
          context: {
            goal,
            what_they_likely_want: wants || null,
            setting: setting || null,
            exposure_points_remaining: Number(exposure) || 3,
            the_gap: gap || null,
          },
        }),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) return { ok: false, error: data.error || `Could not start (HTTP ${res.status})` };
      return { ok: true, job_id: data.job_id, estimated_total_seconds: data.estimated_total_seconds };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  };

  const onJobComplete = (blueprintId, jobRow) => {
    const te = jobRow?.project_context?.te_result || {};
    setPlan({
      blueprint_id: blueprintId || null,
      title: te.title || "Thought Engineering",
      blueprint_markdown: jobRow?.blueprint?.rendered_markdown || "",
      draws_recommended: te.draws_recommended || [],
      logic_checklist: te.logic_checklist || [],
      close_timing: te.close_timing || null,
      engine_version: te.engine_version || jobRow?.blueprint?.layer_4_engine_version || null,
    });
    setGenerationStarted(false);
  };

  if (!session) return <p style={{ padding: 24 }}>Sign in to use Thought Engineering.</p>;

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ marginBottom: 24 }}><Wordmark scale={1.0} /></div>

      {plan ? (
        <PlanView plan={plan} mentions={mentions} session={session} onReset={() => setPlan(null)} />
      ) : generationStarted ? (
        <BlueprintJobView
          start={startJob}
          bearerToken={session.access_token}
          onComplete={onJobComplete}
          onRetry={() => { setGenerationStarted(false); setTimeout(() => setGenerationStarted(true), 0); }}
          surfaceLabel="Thought Engineering plan"
        />
      ) : (
        <div>
          <RecentBlueprintsPanel type="thought_engineering" bearerToken={session.access_token} />
          <SectionHeader index={1} title="Thought Engineering · Strategy & Design" />

          <Callout.Tip>
            Engineer the encounter before the first word. Give me the counterpart and your exact goal, and I'll design the 5-step plan — impression, the molecular draws that pull <em>this</em> person, the logic gaps to close (gaps = traps), and when to time the close — ranked to how they're wired.
          </Callout.Tip>

          <div style={{ display: "grid", gap: 16, marginTop: 20 }}>
            <Field label="Counterpart (mention with @)">
              <MentionAutocomplete
                value={mentionText} onChange={setMentionText}
                mentions={mentions} onMentionsChange={setMentions}
                placeholder="@Dana — a saved profile" rows={2}
                bearerToken={session.access_token}
              />
            </Field>

            <Field label="Your exact goal — the specific outcome / number / ask">
              <input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Sign the $48k annual at the first meeting" style={inputStyle} />
            </Field>

            <Field label="What do they likely want? (optional)">
              <input value={wants} onChange={(e) => setWants(e.target.value)} placeholder="Cover their team, look decisive to their boss" style={inputStyle} />
            </Field>

            <Field label="The setting (optional)">
              <input value={setting} onChange={(e) => setSetting(e.target.value)} placeholder="First pitch / renewal / board ask / coffee" style={inputStyle} />
            </Field>

            <Field label="Exposure points left before the close">
              <input type="number" min="1" max="10" value={exposure} onChange={(e) => setExposure(e.target.value)} style={{ ...inputStyle, width: 110 }} />
            </Field>

            <Field label="The one gap you can't answer well (optional — drives the prebuttal)">
              <textarea value={gap} onChange={(e) => setGap(e.target.value)} rows={2} placeholder="They'll ask why we're pricier than the incumbent" style={inputStyle} />
            </Field>

            {error && <ErrorBox error={error} />}

            <button onClick={beginGeneration} style={terminalActionButtonStyle}>
              Engineer the plan
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PlanView({ plan, mentions, session, onReset }) {
  const draws = plan.draws_recommended || [];
  const laws = plan.logic_checklist || [];
  const storageKey = `te_board_${plan.blueprint_id || "tmp"}`;
  const [checked, setChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || {}; } catch { return {}; }
  });
  const [preInfluence, setPreInfluence] = useState(() => {
    try { return localStorage.getItem(storageKey + "_pi") || ""; } catch { return ""; }
  });
  const toggle = (id) => setChecked((prev) => {
    const next = { ...prev, [id]: !prev[id] };
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch (_) {}
    return next;
  });
  const onPi = (v) => {
    setPreInfluence(v);
    try { localStorage.setItem(storageKey + "_pi", v); } catch (_) {}
  };

  const total = draws.length + laws.length;
  const done = [...draws.map((_, i) => `d${i}`), ...laws.map((_, i) => `l${i}`)].filter((id) => checked[id]).length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <article>
      <h1 style={titleStyle}>{plan.title || "Thought Engineering"}</h1>

      {/* Scoreboard meter */}
      <div style={scoreWrapStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
          <span style={boardHeadStyle}>The board — points set</span>
          <span style={scoreNumStyle}>{done} / {total}</span>
        </div>
        <div style={meterTrackStyle}><div style={{ ...meterFillStyle, width: `${pct}%` }} /></div>
        <p style={scoreHintStyle}>Check each draw as you load it and each gap as you close it. The more points on the board when the conversation starts, the faster it closes.</p>
      </div>

      <div style={{ margin: "18px 0" }}>
        <div style={boardHeadStyle}>Draws loaded</div>
        <div style={{ display: "grid", gap: 8 }}>
          {draws.map((d, i) => {
            const id = `d${i}`;
            return (
              <label key={id} style={{ ...cardStyle, display: "flex", gap: 10, cursor: "pointer", opacity: checked[id] ? 0.6 : 1 }}>
                <input type="checkbox" checked={!!checked[id]} onChange={() => toggle(id)} style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0 }} />
                <span>
                  <div style={cardLabelStyle}>{d.draw}</div>
                  <div style={cardBodyStyle}><strong>Why:</strong> {d.why}<br /><strong>How:</strong> {d.how}</div>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div style={{ margin: "18px 0" }}>
        <div style={boardHeadStyle}>Indisputable logic — gaps = traps</div>
        <div style={{ display: "grid", gap: 6 }}>
          {laws.map((l, i) => {
            const id = `l${i}`;
            return (
              <label key={id} style={{ ...lawRowStyle, borderLeft: `4px solid ${priorityColor(l.priority)}`, cursor: "pointer", opacity: checked[id] ? 0.55 : 1 }}>
                <input type="checkbox" checked={!!checked[id]} onChange={() => toggle(id)} style={{ width: 16, height: 16, flexShrink: 0 }} />
                <span style={lawNameStyle}>{l.law}</span>
                <span style={lawMoveStyle}>{l.move}</span>
                <span style={{ ...priorityBadge, color: priorityColor(l.priority) }}>{l.priority}</span>
              </label>
            );
          })}
        </div>
      </div>

      {plan.close_timing && (
        <div style={{ ...cardStyle, marginBottom: 18 }}>
          <div style={cardLabelStyle}>Time the close (Kairos)</div>
          <div style={cardBodyStyle}>{plan.close_timing}</div>
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <div style={boardHeadStyle}>Points already on the board (Pre-Influence)</div>
        <textarea value={preInfluence} onChange={(e) => onPi(e.target.value)} rows={2}
          placeholder="Prior touches working in your favor before contact — a warm intro, content they've seen, your reputation in their world…"
          style={inputStyle} />
      </div>

      <MarkdownV2 source={plan.blueprint_markdown} subjectNames={mentions.map((m) => m.display_name).filter(Boolean)} />

      {plan.blueprint_id && (
        <BlueprintChat blueprintId={plan.blueprint_id} blueprintType="thought_engineering" presetPrompts={PRESETS} bearerToken={session.access_token} />
      )}

      <div style={{ marginTop: 22 }}>
        <div style={boardHeadStyle}>Take it further — same counterpart</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 8 }}>
          <Link to="/blueprints/negotiation" style={furtherLinkStyle}>Negotiation →</Link>
          <Link to="/blueprints/common-ground" style={furtherLinkStyle}>Explore Alignment →</Link>
          <Link to="/blueprints/pre-meeting" style={furtherLinkStyle}>Pre-Meeting Brief →</Link>
          <Link to="/blueprints/relational" style={furtherLinkStyle}>Relationship Read →</Link>
        </div>
      </div>

      <AuditFooter engineVersion={plan.engine_version} jargonScanPassed={true} blueprintId={plan.blueprint_id} />

      <div style={{ marginTop: 24, display: "flex", justifyContent: "space-between" }}>
        <button onClick={onReset} style={{ ...terminalActionButtonStyle, background: "transparent", color: "var(--ppr-color-accent-navy)" }}>← New plan</button>
        <PrintToPdfButton label="Save as PDF" />
      </div>
    </article>
  );
}

const scoreWrapStyle = {
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  padding: "14px 16px",
  marginTop: 16,
};
const scoreNumStyle = {
  fontFamily: "var(--ppr-typography-font-family-mono)",
  fontSize: "var(--ppr-typography-font-size-lg)",
  color: "var(--ppr-color-accent-navy)",
};
const meterTrackStyle = {
  height: 8,
  background: "var(--ppr-color-background-muted)",
  borderRadius: 4,
  overflow: "hidden",
};
const meterFillStyle = {
  height: "100%",
  background: "var(--ppr-color-accent-coral)",
  transition: "width 0.25s ease",
};
const scoreHintStyle = {
  margin: "8px 0 0",
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  color: "var(--ppr-color-text-muted)",
  lineHeight: 1.5,
};
const furtherLinkStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "0.04em",
  color: "var(--ppr-color-accent-navy)",
  textDecoration: "none",
  border: "1.5px solid var(--ppr-color-border-default)",
  padding: "6px 12px",
};

function priorityColor(p) {
  if (p === "high") return "var(--ppr-color-accent-coral)";
  if (p === "medium") return "var(--ppr-color-accent-navy)";
  return "var(--ppr-color-text-muted)";
}

const inputStyle = {
  padding: "12px 14px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  width: "100%",
  outline: "none",
  resize: "vertical",
};
const titleStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-3xl)",
  fontWeight: "var(--ppr-typography-font-weight-regular)",
  color: "var(--ppr-color-text-primary)",
  letterSpacing: "var(--ppr-typography-letter-spacing-snug)",
  lineHeight: "var(--ppr-typography-line-height-tight)",
  margin: "0 0 8px",
};
const boardHeadStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  color: "var(--ppr-color-text-secondary)",
  marginBottom: 8,
};
const cardStyle = {
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  padding: "12px 14px",
};
const cardLabelStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  color: "var(--ppr-color-accent-coral)",
  marginBottom: 4,
};
const cardBodyStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-base)",
  color: "var(--ppr-color-text-primary)",
  lineHeight: 1.5,
};
const lawRowStyle = {
  display: "flex",
  alignItems: "baseline",
  gap: 12,
  padding: "8px 12px",
  background: "var(--ppr-color-background-surface)",
  border: "1px solid var(--ppr-color-border-default)",
};
const lawNameStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--ppr-color-text-primary)",
  minWidth: 110,
};
const lawMoveStyle = {
  flex: 1,
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  color: "var(--ppr-color-text-secondary)",
};
const priorityBadge = {
  fontFamily: "var(--ppr-typography-font-family-mono)",
  fontSize: "11px",
  textTransform: "uppercase",
};

function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: "block",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        color: "var(--ppr-color-text-secondary)",
        marginBottom: 6,
      }}>{label}</label>
      {children}
    </div>
  );
}

function ErrorBox({ error }) {
  return (
    <div style={{
      padding: "12px 14px",
      border: "2px solid var(--ppr-color-accent-coral)",
      background: "var(--ppr-color-accent-coral-very-soft)",
      color: "var(--ppr-color-accent-coral-dark)",
    }}>
      {error}
    </div>
  );
}
